import { ExternalLink, Share2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { canNativeOpenURL, nativeOpenURL } from "@/lib/hubShell";
import {
  browserTitleForUrl,
  closeHubInAppBrowser,
  subscribeHubInAppBrowser,
} from "@/lib/hubInAppBrowser";

/**
 * Full-screen link viewer with the same toolbar as mail attachments:
 * title, Share, Open with, Close. No sticky bottom sheet.
 */
export function HubInAppBrowserHost() {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => subscribeHubInAppBrowser(setUrl), []);

  if (!url) return null;
  return <HubInAppBrowser url={url} onClose={closeHubInAppBrowser} />;
}

type Props = {
  url: string;
  onClose: () => void;
};

function HubInAppBrowser({ url, onClose }: Props) {
  const [busy, setBusy] = useState<"share" | "open" | "">("");
  const [actionError, setActionError] = useState("");
  const [frameFailed, setFrameFailed] = useState(false);
  const title = browserTitleForUrl(url);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    setActionError("");
    setFrameFailed(false);
  }, [url]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function onShare() {
    if (busy) return;
    setBusy("share");
    setActionError("");
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ url, title });
        return;
      }
      await navigator.clipboard.writeText(url);
      setActionError("Copied. Paste into Safari or another app.");
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(url);
        setActionError("Copied. Paste into Safari or another app.");
      } catch {
        setActionError(err instanceof Error ? err.message : "Could not share.");
      }
    } finally {
      setBusy("");
    }
  }

  async function onOpenWith() {
    if (busy) return;
    setBusy("open");
    setActionError("");
    try {
      if (nativeOpenURL(url)) return;
      if (typeof navigator.share === "function") {
        await navigator.share({ url, title });
        return;
      }
      await navigator.clipboard.writeText(url);
      setActionError("Copied. Paste into Safari to open.");
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      setActionError(err instanceof Error ? err.message : "Could not open this link.");
    } finally {
      setBusy("");
    }
  }

  return createPortal(
    <div
      className="mail-attach-preview-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="mail-attach-preview-sheet" onClick={(event) => event.stopPropagation()}>
        <header className="mail-attach-preview-toolbar">
          <strong className="mail-attach-preview-title" title={url}>
            {title}
          </strong>
          <div className="mail-attach-preview-actions">
            <button
              type="button"
              className="icon-btn"
              aria-label="Share"
              title="Share"
              disabled={Boolean(busy)}
              onClick={() => void onShare()}
            >
              <Share2 size={18} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Open with"
              title={canNativeOpenURL() ? "Open in Safari" : "Open with"}
              disabled={Boolean(busy)}
              onClick={() => void onOpenWith()}
            >
              <ExternalLink size={18} />
            </button>
            <button type="button" className="icon-btn" aria-label="Close" title="Close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </header>
        {actionError ? (
          <p className="mail-attach-preview-error" role="alert">
            {actionError}
          </p>
        ) : null}
        <div className="mail-attach-preview-body mail-attach-preview-body-browser">
          {frameFailed ? (
            <div className="mail-attach-preview-fallback">
              <p>This page cannot preview inside Hub. Use Open with to open it in Safari or another app.</p>
              <p className="mail-attach-preview-url">{url}</p>
              <button
                type="button"
                className="primary-btn"
                disabled={Boolean(busy)}
                onClick={() => void onOpenWith()}
              >
                Open with
              </button>
            </div>
          ) : (
            <iframe
              title={title}
              src={url}
              className="mail-attach-preview-frame"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              onError={() => setFrameFailed(true)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
